import { ComponentType, Dispatch } from "react";
import { UnknownAction } from "@reduxjs/toolkit";
import { z } from "zod";
import { ChatbotUrls, ChatSession, ContextOption } from "../chatbotV2";

export interface TeamMateState {
  showCoachingForm: boolean;
}

export interface TeamMateContextProps {
  state: TeamMateState;
  dispatch: Dispatch<UnknownAction>;
}

export interface CoachAiResponse {
  ai_response: string;
  category: string;
  personalizationScope: string;
}

export interface CoachAiConfirmationResponse {
  ok: boolean;
  train_doc_uid: string;
  frontend_url: string;
}

export enum ContentCategory {
  TERM_CLARIFICATION = "TermClarification",
  GENERAL_GUIDELINES = "GeneralGuidelines",
  BUSINESS_EXPLANATION = "BusinessExplanation",
}

export enum TaskLabels {
  DocGen = "DocGen",
  ChartBot = "ChartBot",
  SqlBot = "SqlExpert",
  OpportunitiesBot = "OpportunitiesBot",
  ProjectGovernor = "ProjectGovernor",
  TeradataToSnowflakeMigrator = "TeradataToSnowflakeMigrator",
  AlertManager = "AlertManager",
}

export enum PersonalizationScope {
  USER_SPECIFIC = "UserSpecific",
  ALL_USERS = "AllUsers",
}

export const learningSchema = z.object({
  train_doc_uid: z.string(),
  userId: z.string(),
  display_name: z.string(),
  taskLabel: z.string(),
  category: z.enum(Object.values(ContentCategory) as [string, ...string[]]),
  personalizationScope: z
    .enum(Object.values(PersonalizationScope) as [string, ...string[]])
    .default(PersonalizationScope.USER_SPECIFIC),
  createdDate: z.string(),
  updatedDate: z.string(),
  content: z
    .string()
    .min(10, { message: "Learning must be at least 10 characters" })
    .max(500, { message: "Learning must not exceed 500 characters" })
    .min(1, { message: "Learning is required" }),
  metadata: z.record(z.unknown()).optional(),
  isActive: z.boolean().default(true),
});

export interface Learning extends z.infer<typeof learningSchema> {}

export enum TeamMateAvailability {
  EXTENSION = "VSCode Extension",
  SAAS = "SaaS",
}

export interface TeamMateComponentProps<T = void> {
  onClose?: (data?: T) => void;
  frontendUrl?: string;
  urls?: ChatbotUrls;
  requestParams?: RequestInit;
  context?: ChatSession["context"];
  contextOptions?: ContextOption[];
}

export interface TeamMateConfig<T = unknown> {
  name: string;
  avatar: string;
  description: string;
  availability: TeamMateAvailability[];
  key: TaskLabels;
  seeInAction?: boolean;
  comingSoon?: boolean | (() => boolean);
  displayComponent?: ComponentType<TeamMateComponentProps<T>>;
  formComponent?: ComponentType<TeamMateComponentProps<T>>;
}

export enum TeamMateActionType {
  SEE_IN_ACTION = "SEE_IN_ACTION",
  REQUEST_ACCESS = "REQUEST_ACCESS",
  VIEW_DETAILS = "VIEW_DETAILS",
}

export const TeamMatesConfig: TeamMateConfig[] = [
  // ... existing code ...
];

export interface Citation {
  id: string;
  content: string;
  taskLabel: TaskLabels;
}
